import { Link } from 'react-router-dom';
import { articles, type Article } from '@/mocks/articles';

const catNameMap: Record<string, string> = {
  specs: '参数曝光',
  renders: '新机谍照',
  price: '价格预测',
  inside: '厂商内幕',
  scene: '上手解读',
};

export function catName(slug: string): string {
  return catNameMap[slug] ?? '数码爆料';
}

interface Props {
  article: Article;
}

export function ArticleMeta({ article }: Props) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
      <span className="inline-flex items-center gap-1">
        <i className="ri-calendar-line" aria-hidden="true" />
        {article.date}
      </span>
      <span className="hidden sm:inline-flex items-center gap-1">
        <i className="ri-time-line" aria-hidden="true" />
        {article.readTime}
      </span>
      <span className="inline-flex items-center gap-1">
        <i className="ri-eye-line" aria-hidden="true" />
        {article.readCount} 阅读
      </span>
    </div>
  );
}

function CategoryBadge({ slug }: { slug: string }) {
  const isRenders = slug === 'renders';
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${
        isRenders ? 'bg-accent-100 text-accent-800' : 'bg-primary-100 text-primary-800'
      }`}
    >
      <i className={isRenders ? 'ri-camera-line' : 'ri-fire-line'} aria-hidden="true" />
      {catName(slug)}
    </span>
  );
}

export default function ArticleCard({ article }: Props) {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-colors hover:border-primary-200">
      <Link to={`/news/${article.id}`} className="relative block aspect-[3/2] w-full overflow-hidden bg-background-200">
        <img
          src={article.image}
          alt={article.imageAlt}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3">
          <CategoryBadge slug={article.category} />
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="font-heading text-base font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700 md:text-lg">
          <Link to={`/news/${article.id}`}>{article.title}</Link>
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-600">{article.summary}</p>
        <div className="mt-auto pt-4">
          <ArticleMeta article={article} />
        </div>
      </div>
    </article>
  );
}

export function FeaturedCard({ article }: Props) {
  return (
    <article className="group relative overflow-hidden rounded-2xl">
      <Link to={`/news/${article.id}`} className="relative block aspect-[16/9] w-full overflow-hidden md:aspect-[16/8]">
        <img src={article.image} alt={article.imageAlt} className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-foreground-950/5" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
          <span className="mb-3 inline-flex">
            <CategoryBadge slug={article.category} />
          </span>
          <h2 className="font-heading text-xl font-bold leading-snug text-background-50 transition-colors group-hover:text-primary-100 md:text-3xl">
            {article.title}
          </h2>
          <p className="mt-2 line-clamp-2 hidden text-sm text-background-200 sm:block md:text-base">{article.summary}</p>
          <div className="mt-3 text-xs text-background-300">
            {article.date} · {article.readTime} · {article.readCount} 阅读
          </div>
        </div>
      </Link>
    </article>
  );
}

export function TrendingRow({ articles: list }: { articles: Article[] }) {
  return (
    <ol className="flex flex-col">
      {list.map((article, idx) => (
        <li key={article.id}>
          <Link to={`/news/${article.id}`} className="group flex items-start gap-3 py-3">
            <span
              className={`mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md font-heading text-sm font-black ${
                idx < 3 ? 'bg-primary-500 text-background-50' : 'bg-secondary-100 text-secondary-800'
              }`}
            >
              {idx + 1}
            </span>
            <span className="flex flex-1 flex-col gap-1">
              <span className="font-heading text-sm font-semibold leading-snug text-foreground-800 transition-colors group-hover:text-primary-700">
                {article.title}
              </span>
              <span className="text-xs text-foreground-500">{article.date}</span>
            </span>
          </Link>
        </li>
      ))}
    </ol>
  );
}

export { articles };